import * as LDClient from "launchdarkly-js-client-sdk";
import configService from "./config";

class LaunchDarklyService {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.initPromise = null;
  }

  async initialize(user = null, forceRetry = false) {
    // If forcing retry or previous init failed, clear the promise and try again
    if (forceRetry && this.initPromise !== null && !this.isInitialized) {
      // ✅ MINIMAL FIX: Check if SDK key exists before allowing forced retry
      const sdkKey = configService.get("ldSdkKey");
      if (!sdkKey) {
        console.warn("Cannot retry initialization: SDK key is not configured");
        return Promise.resolve(false);
      }

      this.initPromise = null;
      if (this.client) {
        try {
          this.client.close();
        } catch (e) {
          // Ignore errors when closing
        }
        this.client = null;
      }
    }

    if (this.initPromise !== null) {
      return this.initPromise;
    }

    const sdkKey = configService.get("ldSdkKey");

    if (!sdkKey) {
      console.warn(
        "LaunchDarkly Client-side ID not found in runtime config (ldSdkKey). Feature flags will default to false."
      );
      this.isInitialized = false;
      // Don't cache the promise when SDK key is missing - allows retry when config loads
      return Promise.resolve(false);
    }

    const promise = new Promise((resolve) => {
      // Create context for LaunchDarkly
      const context = {
        kind: "user",
        key: user?.id || user?.key || "anonymous-user",
        name: user?.name || "Anonymous User",
        email: user?.email || undefined,
        ...user,
      };

      const cleanupFailedInit = () => {
        this.isInitialized = false;
        try {
          if (this.client) {
            this.client.close();
          }
        } catch (e) {
          // ignore
        }
        this.client = null;
        // Defer so `this.initPromise = promise` can run when `failed` fires synchronously
        queueMicrotask(() => {
          this.initPromise = null;
        });
      };

      try {
        this.client = LDClient.initialize(sdkKey, context);

        this.client.on("initialized", () => {
          this.isInitialized = true;
          resolve(true);
        });

        this.client.on("failed", (error) => {
          console.warn("LaunchDarkly SDK failed to initialize:", error);
          cleanupFailedInit();
          resolve(false);
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          if (!this.isInitialized) {
            console.warn("LaunchDarkly SDK initialization timeout");
            cleanupFailedInit();
            resolve(false);
          }
        }, 10000);
      } catch (error) {
        console.warn("Error initializing LaunchDarkly SDK:", error);
        cleanupFailedInit();
        resolve(false);
      }
    });

    this.initPromise = promise;
    return promise;
  }

  async getFlag(flagKey, defaultValue = false) {
    try {
      if (!this.isInitialized || !this.client) {
        console.warn(`LaunchDarkly not initialized. Returning default value for flag: ${flagKey}`);
        return defaultValue;
      }

      await this.client.waitForInitialization(5);
      const flagValue = this.client.variation(flagKey, defaultValue);

      return flagValue;
    } catch (error) {
      if (this.isLaunchDarklyNetworkError(error)) {
        console.warn(`LaunchDarkly flag unavailable (network) "${flagKey}":`, error);
      } else {
        console.error(`Error getting LaunchDarkly flag "${flagKey}":`, error);
      }
      return defaultValue;
    }
  }

  /**
   * Reads a flag without awaiting the SDK.
   *
   * `getFlag` is async (it waits for initialization), which is wrong for hot
   * paths such as the post-mutation account-count refresh: awaiting it would
   * turn a fire-and-forget call into a promise the caller has to hold. The LD
   * client's `variation()` is synchronous, so this returns the flag value as
   * soon as the client is initialized and the caller's default before that.
   *
   * @param {string} flagKey
   * @param {*} defaultValue returned when the client is not ready or throws
   * @returns {*}
   */
  getFlagSync(flagKey, defaultValue) {
    if (!this.isInitialized || !this.client) {
      return defaultValue;
    }

    try {
      return this.client.variation(flagKey, defaultValue);
    } catch (error) {
      console.error(`Error reading LaunchDarkly flag synchronously "${flagKey}":`, error);
      return defaultValue;
    }
  }

  async getAllFlags() {
    try {
      if (!this.isInitialized || !this.client) {
        console.warn("LaunchDarkly not initialized. Returning empty flags object.");
        return {};
      }

      await this.client.waitForInitialization(5);
      return this.client.allFlags();
    } catch (error) {
      if (this.isLaunchDarklyNetworkError(error)) {
        console.warn("LaunchDarkly flags unavailable (network):", error);
      } else {
        console.error("Error getting all LaunchDarkly flags:", error);
      }
      return {};
    }
  }

  // Method to update user context
  async identify(user) {
    if (!this.client) {
      console.warn("LaunchDarkly client not initialized");
      return;
    }

    const context = {
      kind: "user",
      key: user?.id || user?.key || "anonymous-user",
      name: user?.name || "Anonymous User",
      email: user?.email || undefined,
      ...user,
    };

    try {
      await this.client.identify(context);
    } catch (error) {
      console.error("Error updating LaunchDarkly user context:", error);
    }
  }

  // Method to update user context with account as primary tenant context
  async identifyTenant(user, account) {
    if (!this.client) {
      console.warn("LaunchDarkly client not initialized");
      return;
    }

    if (!account || !account.id) {
      console.warn("LaunchDarkly tenant identification requires account information");
      return;
    }

    const context = {
      kind: "user",
      key: `account-${account.id}`,
      name: account.name || `Account ${account.id}`,
      email: account?.owner_email || undefined,
      accountId: account.id,
      accountName: account.name,
      userEmail: user?.email || "",
    };

    try {
      await this.client.identify(context);
    } catch (error) {
      console.error("Error updating LaunchDarkly tenant context:", error);
    }
  }

  isLaunchDarklyNetworkError(error) {
    if (!error) return false;
    if (error.name === "LaunchDarklyFlagFetchError") return true;
    return /network error/i.test(String(error.message ?? ""));
  }

  // Method to close the client
  close() {
    if (this.client) {
      this.client.close();
      this.client = null;
      this.isInitialized = false;
      this.initPromise = null;
    }
  }

  // Method to get current user context
  getCurrentUser() {
    if (!this.client) {
      console.warn("LaunchDarkly client not initialized");
      return null;
    }

    try {
      return this.client.getContext();
    } catch (error) {
      console.error("Error getting LaunchDarkly user context:", error);
      return null;
    }
  }

  // Method to check if current user is anonymous
  isAnonymousUser() {
    const context = this.getCurrentUser();
    if (!context) return true;

    const key = context.key || context.id;
    return key === "anonymous-user" || !key;
  }

  // Method to check if there's a real user context
  hasUserContext() {
    return !this.isAnonymousUser();
  }

  // Method to reset to anonymous user (for logout)
  async resetToAnonymous() {
    if (!this.client) {
      console.warn("LaunchDarkly client not initialized");
      return;
    }

    const anonymousContext = {
      kind: "user",
      key: "anonymous-user",
      name: "Anonymous User",
    };

    try {
      await this.client.identify(anonymousContext);
    } catch (error) {
      console.error("Error resetting LaunchDarkly to anonymous:", error);
    }
  }

  // Method to update user with account information (deprecated - use identifyTenant instead)
  async updateUserWithAccount(user, account) {
    return this.identifyTenant(user, account);
  }
}

// Create singleton instance
const launchDarklyService = new LaunchDarklyService();

export default launchDarklyService;

// Export convenience function
export const getFlag = (flagKey, defaultValue = false) => {
  return launchDarklyService.getFlag(flagKey, defaultValue);
};
